if (!customElements.get('main-product-premium')) {
  customElements.define(
    'main-product-premium',
    class MainProductPremium extends HTMLElement {
      connectedCallback() {
        this.boundVariantClickHandler = this.handleVariantClick.bind(this);
        this.kachingRootObserver = null;
        this.kachingWidgetObserver = null;
        this.kachingWidget = null;
        this.kachingClickHandler = null;
        this.kachingChangeHandler = null;
        this.kachingKeydownHandler = null;
        this.kachingAnimationFrame = null;
        this.isApplyingKachingVisualDiscount = false;
        this.variantRequestController = null;
        this.boughtTogetherSwipers = [];

        this.setupGallery();
        this.setupBoughtTogetherCarousels();
        this.setupVariantPicker();
        this.setupPurchaseOptions();
        this.setupKachingPurchaseOptions();
        this.setupQuantity();
        this.setupTrustpilotScroll();
      }

      disconnectedCallback() {
        this.abortVariantRequest();
        this.destroyGallery();
        this.destroyBoughtTogetherCarousels();
        this.detachKachingWidget();

        if (this.kachingRootObserver) {
          this.kachingRootObserver.disconnect();
          this.kachingRootObserver = null;
        }

        if (this.kachingAnimationFrame) {
          window.cancelAnimationFrame(this.kachingAnimationFrame);
          this.kachingAnimationFrame = null;
        }
      }

      getKachingHost(root = this) {
        if (!root || typeof root.querySelector !== 'function') return null;

        const directHost =
          root.querySelector('kaching-subscriptions') ||
          root.querySelector('kaching-subscriptions-block');

        if (directHost) return directHost;

        const widget = root.querySelector('.kaching-subscriptions__widget');
        return widget?.closest?.('kaching-subscriptions, kaching-subscriptions-block') || widget || null;
      }

      getKachingSlot() {
        return this.querySelector('[data-kaching-slot]');
      }

      normalizeKachingPlacement() {
        const slot = this.getKachingSlot();
        const host = this.getKachingHost();

        if (!slot || !host || slot.contains(host)) return;

        slot.append(host);
      }

      getVisualDiscountConfig() {
        const atcBlock = this.querySelector('.main-product-premium__block--atc');
        const percent = Number.parseFloat(atcBlock?.dataset.visualDiscountPercent || '0');
        const applies =
          atcBlock?.dataset.visualDiscountApplies === 'true' &&
          Number.isFinite(percent) &&
          percent > 0;

        return {
          applies,
          percent: applies ? Math.min(Math.max(percent, 0), 100) : 0,
        };
      }

      parseMoneyLabel(label) {
        const source = String(label || '').trim();
        const amountMatch = source.match(/[0-9](?:[0-9\s.,'’]*[0-9])?/);
        if (!amountMatch) return null;

        const amount = amountMatch[0].replace(/\s/g, '').replace(/[’']/g, '');
        const lastComma = amount.lastIndexOf(',');
        const lastDot = amount.lastIndexOf('.');
        const decimalIndex = Math.max(lastComma, lastDot);
        let decimalSeparator = '';
        let decimals = 0;
        let whole = amount;
        let fraction = '';

        if (decimalIndex > -1) {
          const possibleFraction = amount.slice(decimalIndex + 1).replace(/\D/g, '');

          if (possibleFraction.length > 0 && possibleFraction.length <= 2) {
            decimalSeparator = amount[decimalIndex];
            decimals = possibleFraction.length;
            whole = amount.slice(0, decimalIndex);
            fraction = possibleFraction;
          }
        }

        const wholeDigits = whole.replace(/\D/g, '') || '0';
        const cents =
          Number.parseInt(wholeDigits, 10) * 100 +
          (decimals > 0 ? Number.parseInt(fraction.padEnd(2, '0').slice(0, 2), 10) : 0);

        if (!Number.isFinite(cents)) return null;

        return {
          cents,
          decimals,
          decimalSeparator,
          prefix: source.slice(0, amountMatch.index),
          suffix: source.slice(amountMatch.index + amountMatch[0].length),
        };
      }

      formatDiscountedMoneyLabel(label, percent) {
        const parsed = this.parseMoneyLabel(label);
        if (!parsed) return '';

        const discountedCents = Math.round(parsed.cents * (100 - percent) / 100);
        let amount = (discountedCents / 100).toFixed(parsed.decimals);

        if (parsed.decimalSeparator === ',') {
          amount = amount.replace('.', ',');
        }

        return `${parsed.prefix}${amount}${parsed.suffix}`;
      }

      upsertKachingComparePrice(priceNode, originalLabel) {
        let compareNode = priceNode.nextElementSibling;

        if (!compareNode || !compareNode.classList.contains('main-product-premium__kaching-compare')) {
          compareNode = document.createElement('s');
          compareNode.className = 'main-product-premium__kaching-compare';
          priceNode.insertAdjacentElement('afterend', compareNode);
        }

        if (compareNode.textContent.trim() !== originalLabel) {
          compareNode.textContent = originalLabel;
        }
      }

      applyKachingVisualDiscount() {
        if (!this.kachingWidget || this.isApplyingKachingVisualDiscount) return;

        const discountConfig = this.getVisualDiscountConfig();
        if (!discountConfig.applies) return;

        const oneTimeCards = Array.from(this.kachingWidget.querySelectorAll('.kaching-subscriptions__card-wrapper.one-time-purchase'));
        const priceNodes =
          oneTimeCards.length > 0
            ? oneTimeCards.flatMap((card) =>
                Array.from(card.querySelectorAll('[data-testid="one-time-price"], .kaching-subscriptions__card-price'))
              )
            : Array.from(this.kachingWidget.querySelectorAll('[data-testid="one-time-price"]'));

        if (!priceNodes.length) return;

        this.isApplyingKachingVisualDiscount = true;

        try {
          const seenNodes = new Set();

          priceNodes.forEach((priceNode) => {
            if (!priceNode || seenNodes.has(priceNode)) return;
            seenNodes.add(priceNode);

            const storedOriginalLabel = (priceNode.dataset.visualDiscountOriginalLabel || '').trim();
            const currentLabel = (priceNode.textContent || '').trim();
            const storedDiscountedLabel = storedOriginalLabel
              ? this.formatDiscountedMoneyLabel(storedOriginalLabel, discountConfig.percent)
              : '';
            const originalLabel =
              storedOriginalLabel && (currentLabel === storedOriginalLabel || currentLabel === storedDiscountedLabel)
                ? storedOriginalLabel
                : currentLabel;

            if (!originalLabel) return;

            const discountedLabel = this.formatDiscountedMoneyLabel(originalLabel, discountConfig.percent);
            if (!discountedLabel || discountedLabel === originalLabel) return;

            priceNode.dataset.visualDiscountOriginalLabel = originalLabel;

            if (priceNode.textContent.trim() !== discountedLabel) {
              priceNode.textContent = discountedLabel;
            }

            this.upsertKachingComparePrice(priceNode, originalLabel);
          });
        } finally {
          this.isApplyingKachingVisualDiscount = false;
        }
      }

      destroyGallery() {
        if (this.gallerySwiper) {
          this.gallerySwiper.destroy(true, true);
          this.gallerySwiper = null;
        }

        if (this.galleryThumbsSwiper) {
          this.galleryThumbsSwiper.destroy(true, true);
          this.galleryThumbsSwiper = null;
        }

        this.galleryMediaIndexById = null;
      }

      destroyBoughtTogetherCarousels() {
        if (!Array.isArray(this.boughtTogetherSwipers)) {
          this.boughtTogetherSwipers = [];
          return;
        }

        this.boughtTogetherSwipers.forEach((swiper) => {
          if (swiper && typeof swiper.destroy === 'function') {
            swiper.destroy(true, true);
          }
        });

        this.boughtTogetherSwipers = [];
      }

      setupGallery() {
        this.destroyGallery();

        const galleryRoot = this.querySelector('[data-gallery-root]');
        if (!galleryRoot || typeof window.Swiper === 'undefined') return;

        const mainEl = galleryRoot.querySelector('[data-gallery-swiper]');
        if (!mainEl) return;

        const thumbsEl = galleryRoot.querySelector('[data-gallery-thumbs]');
        const prevEl = galleryRoot.querySelector('[data-gallery-prev]');
        const nextEl = galleryRoot.querySelector('[data-gallery-next]');
        const thumbButtons = Array.from(galleryRoot.querySelectorAll('[data-gallery-thumb]'));
        const computedStyles = window.getComputedStyle(galleryRoot);
        const thumbsGap = Number.parseFloat(computedStyles.getPropertyValue('--main-product-premium-thumbnails-gap')) || 10;

        let thumbsSwiper = null;

        if (thumbsEl) {
          thumbsSwiper = new window.Swiper(thumbsEl, {
            slidesPerView: 'auto',
            spaceBetween: thumbsGap,
            watchSlidesProgress: true,
            freeMode: true,
            watchOverflow: true,
          });

          this.galleryThumbsSwiper = thumbsSwiper;
        }

        const swiperOptions = {
          slidesPerView: 1,
          speed: 520,
          watchOverflow: true,
        };

        if (prevEl && nextEl) {
          swiperOptions.navigation = {
            prevEl,
            nextEl,
          };
        }

        this.gallerySwiper = new window.Swiper(mainEl, swiperOptions);
        this.galleryMediaIndexById = new Map(
          Array.from(galleryRoot.querySelectorAll('[data-media-id]')).map((slide, index) => [slide.dataset.mediaId, index])
        );

        const syncThumbState = () => {
          if (!thumbButtons.length) return;

          const activeIndex = this.gallerySwiper.realIndex;
          thumbButtons.forEach((button, index) => {
            const isActive = index === activeIndex;
            button.classList.toggle('is-active', isActive);
            button.setAttribute('aria-current', isActive ? 'true' : 'false');
          });

          if (thumbsSwiper) {
            thumbsSwiper.slideTo(activeIndex);
          }
        };

        if (thumbButtons.length) {
          thumbButtons.forEach((button) => {
            button.addEventListener('click', (event) => {
              event.preventDefault();
              const index = Number.parseInt(button.dataset.mediaIndex || '', 10);
              if (!Number.isFinite(index)) return;
              this.gallerySwiper.slideTo(index);
            });
          });
        }

        this.gallerySwiper.on('slideChange', syncThumbState);

        const selectedMediaId = galleryRoot.dataset.selectedMediaId;
        if (selectedMediaId) {
          this.slideGalleryToMedia(selectedMediaId, 0);
        }

        syncThumbState();
      }

      setupBoughtTogetherCarousels() {
        this.destroyBoughtTogetherCarousels();

        if (typeof window.Swiper === 'undefined') return;

        const carouselRoots = Array.from(this.querySelectorAll('[data-bought-together-carousel]'));
        if (!carouselRoots.length) return;

        carouselRoots.forEach((root) => {
          const sliderEl = root.querySelector('[data-bought-together-slider]');
          if (!sliderEl) return;

          const prevEl = root.querySelector('[data-bought-together-prev]');
          const nextEl = root.querySelector('[data-bought-together-next]');
          const paginationEl = root.querySelector('[data-bought-together-pagination]');
          const computedStyles = window.getComputedStyle(root);
          const gap = Number.parseFloat(computedStyles.getPropertyValue('--main-product-premium-bought-gap')) || 12;

          const swiperOptions = {
            slidesPerView: 1,
            spaceBetween: gap,
            speed: 520,
            autoHeight: true,
            watchOverflow: true,
            grabCursor: true,
            observer: true,
            observeParents: true,
            updateOnWindowResize: true,
          };

          if (prevEl && nextEl) {
            swiperOptions.navigation = {
              prevEl,
              nextEl,
            };
          }

          if (paginationEl) {
            swiperOptions.pagination = {
              el: paginationEl,
              clickable: true,
            };
          }

          const swiper = new window.Swiper(sliderEl, swiperOptions);

          const syncArrowState = () => {
            if (prevEl) {
              const hidePrev = swiper.isLocked || swiper.isBeginning;
              prevEl.classList.toggle('is-hidden', hidePrev);
              prevEl.disabled = hidePrev;
              prevEl.setAttribute('aria-hidden', hidePrev ? 'true' : 'false');
            }

            if (nextEl) {
              const hideNext = swiper.isLocked || swiper.isEnd;
              nextEl.classList.toggle('is-hidden', hideNext);
              nextEl.disabled = hideNext;
              nextEl.setAttribute('aria-hidden', hideNext ? 'true' : 'false');
            }
          };

          swiper.on('slideChange', syncArrowState);
          swiper.on('update', syncArrowState);
          swiper.on('resize', syncArrowState);
          swiper.on('lock', syncArrowState);
          swiper.on('unlock', syncArrowState);
          swiper.on('fromEdge', syncArrowState);
          swiper.on('reachBeginning', syncArrowState);
          swiper.on('reachEnd', syncArrowState);
          syncArrowState();

          this.boughtTogetherSwipers.push(swiper);
        });
      }

      setupVariantPicker() {
        const variantLinks = Array.from(this.querySelectorAll('[data-variant-id][href]'));
        if (!variantLinks.length) return;

        variantLinks.forEach((link) => {
          link.addEventListener('click', this.boundVariantClickHandler);
        });
      }

      slideGalleryToMedia(mediaId, speed = 520) {
        if (!this.gallerySwiper || !mediaId || !this.galleryMediaIndexById) return;

        const slideIndex = this.galleryMediaIndexById.get(String(mediaId));
        if (!Number.isInteger(slideIndex)) return;

        this.gallerySwiper.slideTo(slideIndex, speed);
      }

      abortVariantRequest() {
        if (!this.variantRequestController) return;

        this.variantRequestController.abort();
        this.variantRequestController = null;
      }

      async handleVariantClick(event) {
        const link = event.currentTarget;

        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

        event.preventDefault();

        if (link.classList.contains('is-active')) {
          this.scrollGalleryToStart();
          return;
        }

        const targetUrl = new URL(link.href, window.location.origin);
        const nextState = {
          quantity: this.getCurrentQuantityValue(),
          planType: this.getCurrentPlanType(),
          sellingPlanId: this.getCurrentSellingPlanId(),
        };

        this.classList.add('is-loading');

        try {
          const nextRoot = await this.fetchVariantSection(targetUrl);
          if (!nextRoot) {
            window.location = targetUrl.toString();
            return;
          }

          this.replaceSectionContent(nextRoot, nextState);
          window.history.replaceState({}, '', `${targetUrl.pathname}${targetUrl.search}`);
          this.scrollGalleryToStart();
        } catch (error) {
          if (error?.name === 'AbortError') return;

          console.error(error);
          window.location = targetUrl.toString();
        } finally {
          this.classList.remove('is-loading');
        }
      }

      async fetchVariantSection(targetUrl) {
        const sectionId = this.dataset.sectionId;
        if (!sectionId) return null;

        this.abortVariantRequest();

        const requestUrl = new URL(targetUrl.toString());
        requestUrl.searchParams.set('section_id', sectionId);

        const controller = new AbortController();
        this.variantRequestController = controller;

        try {
          const response = await fetch(requestUrl.toString(), {
            signal: controller.signal,
            headers: {
              'X-Requested-With': 'XMLHttpRequest',
            },
          });

          if (!response.ok) {
            throw new Error(`Failed to load variant section: ${response.status}`);
          }

          const html = await response.text();
          if (controller.signal.aborted) return null;

          const parser = new DOMParser();
          const documentFragment = parser.parseFromString(html, 'text/html');
          const nextRoot = documentFragment.querySelector('main-product-premium');
          return nextRoot;
        } finally {
          if (this.variantRequestController === controller) {
            this.variantRequestController = null;
          }
        }
      }

      replaceSectionContent(nextRoot, nextState = {}) {
        const preservedSubscriptionRoot = this.capturePreservedSubscriptionRoot();

        this.destroyGallery();
        this.destroyBoughtTogetherCarousels();
        this.detachKachingWidget();

        if (this.kachingRootObserver) {
          this.kachingRootObserver.disconnect();
          this.kachingRootObserver = null;
        }

        if (this.kachingAnimationFrame) {
          window.cancelAnimationFrame(this.kachingAnimationFrame);
          this.kachingAnimationFrame = null;
        }

        this.innerHTML = nextRoot.innerHTML;

        if (nextRoot.dataset.sectionId) {
          this.dataset.sectionId = nextRoot.dataset.sectionId;
        }

        this.restorePreservedSubscriptionRoot(preservedSubscriptionRoot, nextRoot);

        this.setupGallery();
        this.setupBoughtTogetherCarousels();
        this.setupVariantPicker();
        this.setupPurchaseOptions();
        this.setupKachingPurchaseOptions();
        this.setupQuantity();
        this.setupTrustpilotScroll();
        this.restoreQuantityValue(nextState.quantity);
        this.restorePurchaseState(nextState);
      }

      capturePreservedSubscriptionRoot() {
        const subscriptionRoot = this.querySelector('[data-subscription-root]');
        if (subscriptionRoot?.querySelector('kaching-subscriptions, .kaching-subscriptions__widget')) {
          return {
            mode: 'subscription-root',
            node: subscriptionRoot,
          };
        }

        const kachingHost = this.getKachingHost();

        if (!kachingHost) return null;

        return {
          mode: 'detached-kaching-host',
          node: kachingHost,
        };
      }

      restorePreservedSubscriptionRoot(preservedSubscriptionRoot, nextRoot) {
        if (!preservedSubscriptionRoot?.node) return;

        if (this.getKachingHost()) return;

        if (preservedSubscriptionRoot.mode === 'subscription-root') {
          const nextSubscriptionRoot = this.querySelector('[data-subscription-root]');
          if (!nextSubscriptionRoot) return;

          this.syncPreservedSubscriptionRoot(preservedSubscriptionRoot.node, nextSubscriptionRoot, nextRoot);
          nextSubscriptionRoot.replaceWith(preservedSubscriptionRoot.node);
          return;
        }

        const kachingSlot = this.getKachingSlot();
        const atcBlock = this.querySelector('.main-product-premium__block--atc');
        if (!kachingSlot && !atcBlock) return;

        this.syncPreservedSubscriptionRoot(preservedSubscriptionRoot.node, null, nextRoot);
        if (kachingSlot) {
          kachingSlot.append(preservedSubscriptionRoot.node);
          return;
        }

        atcBlock.prepend(preservedSubscriptionRoot.node);
      }

      syncPreservedSubscriptionRoot(preservedSubscriptionRoot, nextSubscriptionRoot, nextRoot) {
        const preservedQuantityInput = preservedSubscriptionRoot.querySelector('[data-quantity-input]');
        const nextQuantityInput = nextSubscriptionRoot?.querySelector('[data-quantity-input]');

        if (preservedQuantityInput && nextQuantityInput) {
          ['min', 'max', 'step'].forEach((attributeName) => {
            const nextValue = nextQuantityInput.getAttribute(attributeName);

            if (nextValue === null) {
              preservedQuantityInput.removeAttribute(attributeName);
            } else {
              preservedQuantityInput.setAttribute(attributeName, nextValue);
            }
          });
        }

        const nextVariantId =
          nextRoot.querySelector('input[name="id"]')?.value || this.querySelector('input[name="id"]')?.value || '';

        if (!nextVariantId) return;

        const kachingElements = [];
        if (preservedSubscriptionRoot.matches?.('kaching-subscriptions, kaching-subscriptions-block')) {
          kachingElements.push(preservedSubscriptionRoot);
        }
        kachingElements.push(...preservedSubscriptionRoot.querySelectorAll('kaching-subscriptions, kaching-subscriptions-block'));

        kachingElements.forEach((element) => {
          element.setAttribute('current-variant-id', nextVariantId);
        });

        const kachingBlock =
          preservedSubscriptionRoot.matches?.('kaching-subscriptions-block')
            ? preservedSubscriptionRoot
            : preservedSubscriptionRoot.querySelector('kaching-subscriptions-block');
        const productConfig = kachingBlock?.getAttribute('product');
        if (!productConfig) return;

        try {
          const parsedProductConfig = JSON.parse(productConfig);
          parsedProductConfig.selectedVariantId = Number(nextVariantId) || nextVariantId;
          kachingBlock.setAttribute('product', JSON.stringify(parsedProductConfig));
        } catch (error) {
          // Ignore malformed app payloads and keep the mounted widget alive.
        }
      }

      getCurrentQuantityValue() {
        return this.querySelector('[data-quantity-input]')?.value || '';
      }

      restoreQuantityValue(value) {
        if (value === '') return;

        const input = this.querySelector('[data-quantity-input]');
        if (!input) return;

        const parsedValue = Number(value);
        if (!Number.isFinite(parsedValue)) return;

        const min = Number(input.getAttribute('min')) || 1;
        const maxAttr = input.getAttribute('max');
        const max = maxAttr === null || maxAttr === '' ? Number.MAX_SAFE_INTEGER : Number(maxAttr) || Number.MAX_SAFE_INTEGER;
        const step = Number(input.getAttribute('step')) || 1;
        const clampedValue = Math.max(min, Math.min(max, parsedValue));
        const remainder = (clampedValue - min) % step;
        const normalizedValue = remainder === 0 ? clampedValue : clampedValue - remainder;

        input.value = String(Math.max(min, normalizedValue));
      }

      getCurrentPlanType() {
        return this.querySelector('[data-plan-input]:checked')?.value || '';
      }

      getCurrentSellingPlanId() {
        const frequencySelect = this.querySelector('[data-plan-frequency-select]');
        if (frequencySelect && frequencySelect.value) {
          return frequencySelect.value;
        }

        return this.querySelector('[data-selling-plan-input]')?.value || '';
      }

      restorePurchaseState(nextState = {}) {
        if (!nextState.planType || !this.planInputs || !this.planInputs.length) return;

        const matchingInput = this.planInputs.find(
          (input) => input.value === nextState.planType && !input.disabled
        );

        if (!matchingInput) {
          this.syncPlanState();
          return;
        }

        matchingInput.checked = true;

        if (nextState.planType === 'subscribe' && nextState.sellingPlanId && this.planFrequencySelect) {
          const matchingOption = Array.from(this.planFrequencySelect.options).find(
            (option) => option.value === nextState.sellingPlanId
          );

          if (matchingOption) {
            this.planFrequencySelect.value = matchingOption.value;
          }
        }

        this.syncPlanState();
      }

      scrollGalleryToStart() {
        window.requestAnimationFrame(() => {
          if (this.gallerySwiper) {
            this.gallerySwiper.slideTo(0, 0);
          }

          if (this.galleryThumbsSwiper) {
            this.galleryThumbsSwiper.slideTo(0, 0);
          }

          const galleryMain = this.querySelector('.main-product-premium__gallery-main');
          if (!galleryMain) return;

          galleryMain.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        });
      }

      setupPurchaseOptions() {
        this.sellingPlanInput = this.querySelector('[data-selling-plan-input]');
        this.atcPriceNodes = Array.from(this.querySelectorAll('[data-atc-price]'));
        this.planFrequencySelect = this.querySelector('[data-plan-frequency-select]');
        this.planFrequencyWrap = this.querySelector('[data-plan-frequency-wrap]');
        this.planFrequencyDropdown = this.querySelector('[data-plan-frequency-dropdown]');
        this.planFrequencyToggle = this.querySelector('[data-plan-frequency-toggle]');
        this.planFrequencyOptions = Array.from(this.querySelectorAll('[data-plan-frequency-option]'));
        this.subscriptionRoot = this.querySelector('[data-subscription-root]');
        this.planInputs = Array.from(this.querySelectorAll('[data-plan-input]'));
        if (!this.planInputs.length) return;

        this.planInputs.forEach((input) => {
          if (input.dataset.planStateBound === 'true') return;

          input.addEventListener('change', () => this.syncPlanState());
          input.dataset.planStateBound = 'true';
        });

        if (this.planFrequencySelect) {
          if (this.planFrequencySelect.dataset.planFrequencyBound !== 'true') {
            this.planFrequencySelect.addEventListener('change', () => {
              const subscribeInput = this.planInputs.find((input) => input.value === 'subscribe' && !input.disabled);
              if (subscribeInput) subscribeInput.checked = true;
              this.syncPlanState();
            });
            this.planFrequencySelect.dataset.planFrequencyBound = 'true';
          }
        }

        if (this.planFrequencyToggle && this.planFrequencyToggle.dataset.planFrequencyToggleBound !== 'true') {
          this.planFrequencyToggle.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            this.togglePlanFrequencyMenu();
          });

          this.planFrequencyToggle.addEventListener('keydown', (event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              this.togglePlanFrequencyMenu();
              return;
            }

            if (event.key === 'Escape') {
              event.preventDefault();
              this.setPlanFrequencyMenuOpen(false);
            }
          });

          this.planFrequencyToggle.dataset.planFrequencyToggleBound = 'true';
        }

        this.planFrequencyOptions.forEach((option) => {
          if (option.dataset.planFrequencyOptionBound === 'true') return;

          option.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            this.selectPlanFrequencyOption(option);
          });

          option.addEventListener('keydown', (event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              this.selectPlanFrequencyOption(option);
              return;
            }

            if (event.key === 'Escape') {
              event.preventDefault();
              this.setPlanFrequencyMenuOpen(false);
              this.planFrequencyToggle?.focus();
            }
          });

          option.dataset.planFrequencyOptionBound = 'true';
        });

        if (this.dataset.planFrequencyOutsideBound !== 'true') {
          this.addEventListener('click', (event) => {
            const dropdown = this.querySelector('[data-plan-frequency-dropdown]');
            if (!dropdown || dropdown.contains(event.target)) return;
            this.setPlanFrequencyMenuOpen(false);
          });
          this.dataset.planFrequencyOutsideBound = 'true';
        }

        this.syncPlanState();
      }

      togglePlanFrequencyMenu() {
        const subscribeInput = this.planInputs?.find((input) => input.value === 'subscribe' && !input.disabled);
        if (subscribeInput && !subscribeInput.checked) {
          subscribeInput.checked = true;
          this.syncPlanState();
        }

        const dropdown = this.querySelector('[data-plan-frequency-dropdown]');
        const isOpen = dropdown?.classList.contains('is-open');
        this.setPlanFrequencyMenuOpen(!isOpen);
      }

      setPlanFrequencyMenuOpen(isOpen) {
        const dropdown = this.querySelector('[data-plan-frequency-dropdown]');
        const toggle = this.querySelector('[data-plan-frequency-toggle]');
        if (!dropdown) return;

        const isDisabled =
          this.querySelector('[data-plan-frequency-wrap]')?.classList.contains('is-disabled') ||
          this.querySelector('[data-plan-frequency-select]')?.disabled;
        const shouldOpen = Boolean(isOpen) && !isDisabled;

        dropdown.classList.toggle('is-open', shouldOpen);
        if (toggle) {
          toggle.setAttribute('aria-expanded', shouldOpen ? 'true' : 'false');
        }
      }

      selectPlanFrequencyOption(option) {
        if (!option) return;

        const value = option.dataset.value || '';
        const select = this.querySelector('[data-plan-frequency-select]');
        const subscribeInput = this.planInputs?.find((input) => input.value === 'subscribe' && !input.disabled);

        if (subscribeInput) {
          subscribeInput.checked = true;
        }

        if (select && value) {
          select.value = value;
        }

        this.syncPlanState();
        this.setPlanFrequencyMenuOpen(false);
        this.planFrequencyToggle?.focus();
      }

      setupKachingPurchaseOptions() {
        const syncFromWidget = () => {
          if (this.isApplyingKachingVisualDiscount) return;
          if (this.kachingAnimationFrame) return;

          this.kachingAnimationFrame = window.requestAnimationFrame(() => {
            this.kachingAnimationFrame = null;
            this.applyKachingVisualDiscount();
            this.syncKachingState();
          });
        };

        const bindWidget = (widget) => {
          this.detachKachingWidget();
          this.kachingWidget = widget;

          this.kachingClickHandler = syncFromWidget;
          this.kachingChangeHandler = syncFromWidget;
          this.kachingKeydownHandler = (event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              window.setTimeout(syncFromWidget, 0);
            }
          };

          widget.addEventListener('click', this.kachingClickHandler);
          widget.addEventListener('change', this.kachingChangeHandler);
          widget.addEventListener('keydown', this.kachingKeydownHandler);

          this.kachingWidgetObserver = new MutationObserver(syncFromWidget);
          this.kachingWidgetObserver.observe(widget, {
            subtree: true,
            childList: true,
            characterData: true,
            attributes: true,
            attributeFilter: ['class', 'data-selling-plan-id'],
          });

          this.applyKachingVisualDiscount();
          this.syncKachingState();
        };

        const attachWidgetIfNeeded = () => {
          this.normalizeKachingPlacement();
          const widget = this.querySelector('.kaching-subscriptions__widget');
          if (!widget) return;
          if (widget === this.kachingWidget) return;
          bindWidget(widget);
        };

        attachWidgetIfNeeded();

        this.kachingRootObserver = new MutationObserver(() => {
          if (this.kachingWidget && !this.contains(this.kachingWidget)) {
            this.detachKachingWidget();
          }
          attachWidgetIfNeeded();
        });

        this.kachingRootObserver.observe(this, {
          subtree: true,
          childList: true,
        });
      }

      detachKachingWidget() {
        if (this.kachingWidgetObserver) {
          this.kachingWidgetObserver.disconnect();
          this.kachingWidgetObserver = null;
        }

        if (this.kachingWidget) {
          if (this.kachingClickHandler) {
            this.kachingWidget.removeEventListener('click', this.kachingClickHandler);
          }
          if (this.kachingChangeHandler) {
            this.kachingWidget.removeEventListener('change', this.kachingChangeHandler);
          }
          if (this.kachingKeydownHandler) {
            this.kachingWidget.removeEventListener('keydown', this.kachingKeydownHandler);
          }
        }

        this.kachingWidget = null;
        this.kachingClickHandler = null;
        this.kachingChangeHandler = null;
        this.kachingKeydownHandler = null;
      }

      syncKachingState() {
        if (!this.kachingWidget) return;

        const widget = this.kachingWidget;
        const selectedCard = widget.querySelector('.kaching-subscriptions__cards-wrapper > .kaching-subscriptions__card-wrapper.selected');
        const isOneTimeSelected = selectedCard ? selectedCard.classList.contains('one-time-purchase') : false;

        let selectedPriceLabel = '';
        let selectedSellingPlanId = '';

        const getNodeText = (node) => (node ? String(node.textContent || '').trim() : '');
        const getFirstTextBySelectors = (root, selectors = []) => {
          if (!root || !selectors.length) return '';

          for (const selector of selectors) {
            const node = root.querySelector(selector);
            const text = getNodeText(node);
            if (text) return text;
          }

          return '';
        };

        if (isOneTimeSelected) {
          selectedPriceLabel = getFirstTextBySelectors(selectedCard || widget, [
            '.kaching-subscriptions__card-price',
            '[data-testid="one-time-price"]',
          ]);
        } else {
          selectedSellingPlanId = widget.dataset.sellingPlanId || '';
          selectedPriceLabel = getFirstTextBySelectors(selectedCard || widget, [
            '.kaching-subscriptions__dropdown-trigger-price',
            '.kaching-subscriptions__selling-plan-price',
            '.kaching-subscriptions__card-price',
          ]);

          if (!selectedPriceLabel) {
            const selectedSellingPlan = widget.querySelector(
              '.kaching-subscriptions__selling-plan .kaching-subscriptions__card-radio.selected'
            );
            const selectedSellingPlanRow = selectedSellingPlan
              ? selectedSellingPlan.closest('.kaching-subscriptions__selling-plan')
              : null;

            selectedPriceLabel = getFirstTextBySelectors(selectedSellingPlanRow || widget, [
              '.kaching-subscriptions__selling-plan-price',
              '.kaching-subscriptions__dropdown-trigger-price',
              '.kaching-subscriptions__card-price',
            ]);
          }
        }

        if (!selectedPriceLabel) return;

        if (this.sellingPlanInput) {
          this.sellingPlanInput.value = selectedSellingPlanId;
        }

        this.atcPriceNodes.forEach((node) => {
          node.textContent = selectedPriceLabel;
        });
      }

      syncPlanState() {
        if (!this.planInputs || !this.planInputs.length) return;

        let checkedInput = this.planInputs.find((input) => input.checked && !input.disabled);

        if (!checkedInput) {
          checkedInput = this.planInputs.find((input) => !input.disabled);
          if (!checkedInput) return;
          checkedInput.checked = true;
        }

        const isSubscribe = checkedInput.value === 'subscribe';
        let selectedPriceLabel = checkedInput.dataset.priceLabel || '';
        let selectedSellingPlanId = checkedInput.dataset.sellingPlanId || '';
        let selectedFrequencyOption = null;
        let selectedFrequencyPriceLabel = '';

        if (isSubscribe && this.planFrequencySelect) {
          const selectedOption = this.planFrequencySelect.options[this.planFrequencySelect.selectedIndex];
          if (selectedOption) {
            selectedFrequencyOption = selectedOption;
            selectedFrequencyPriceLabel = selectedOption.dataset.priceLabel || selectedPriceLabel;
            selectedSellingPlanId = selectedOption.value || selectedSellingPlanId;
            selectedPriceLabel = selectedOption.dataset.priceLabel || selectedPriceLabel;
          }
        } else if (this.planFrequencySelect) {
          selectedFrequencyOption = this.planFrequencySelect.options[this.planFrequencySelect.selectedIndex];
          selectedFrequencyPriceLabel = selectedFrequencyOption?.dataset.priceLabel || '';
        }

        if (this.sellingPlanInput) {
          this.sellingPlanInput.value = selectedSellingPlanId;
        }

        this.atcPriceNodes.forEach((node) => {
          node.textContent = selectedPriceLabel;
        });

        this.planInputs.forEach((input) => {
          const card = input.closest('[data-plan-card]');
          if (card) card.classList.toggle('is-selected', input === checkedInput);
        });

        if (this.planFrequencySelect) {
          this.planFrequencySelect.disabled = !isSubscribe;
        }

        if (this.planFrequencyWrap) {
          this.planFrequencyWrap.classList.toggle('is-disabled', !isSubscribe);
        }

        if (!isSubscribe) {
          this.setPlanFrequencyMenuOpen(false);
        }

        this.updateSubscriptionFrequencyDisplay(selectedFrequencyOption, selectedFrequencyPriceLabel);

        if (this.subscriptionRoot) {
          this.subscriptionRoot.classList.toggle('is-subscribe-selected', isSubscribe);
        }
      }

      updateSubscriptionFrequencyDisplay(selectedOption, priceLabel = '') {
        const labelNode = this.querySelector('[data-plan-frequency-label]');
        const priceNode = this.querySelector('[data-plan-frequency-price]');

        if (labelNode && selectedOption) {
          labelNode.textContent = selectedOption.dataset.displayLabel || String(selectedOption.textContent || '').trim();
        }

        if (priceNode && priceLabel) {
          priceNode.textContent = priceLabel;
        }

        const selectedValue = selectedOption?.value || '';
        this.querySelectorAll('[data-plan-frequency-option]').forEach((option) => {
          const isSelected = Boolean(selectedValue) && option.dataset.value === selectedValue;
          option.classList.toggle('is-selected', isSelected);
          option.setAttribute('aria-selected', isSelected ? 'true' : 'false');
        });
      }

      setupQuantity() {
        const input = this.querySelector('[data-quantity-input]');
        if (!input) return;

        const minusButton = this.querySelector('[data-qty-minus]');
        const plusButton = this.querySelector('[data-qty-plus]');

        const getNumber = (value, fallback) => {
          const parsed = Number(value);
          return Number.isFinite(parsed) ? parsed : fallback;
        };

        const getLimits = () => {
          const min = getNumber(input.getAttribute('min'), 1);
          const maxAttr = input.getAttribute('max');
          const hasMax = maxAttr !== null && maxAttr !== '';
          const max = hasMax ? getNumber(maxAttr, Number.MAX_SAFE_INTEGER) : Number.MAX_SAFE_INTEGER;
          const step = Math.max(getNumber(input.getAttribute('step'), 1), 1);

          return { min, max, step };
        };

        const setValue = (nextValue, dispatchChange = true) => {
          const { min, max } = getLimits();
          const clamped = Math.max(min, Math.min(max, nextValue));
          input.value = String(clamped);
          if (dispatchChange) {
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        };

        if (minusButton) {
          if (minusButton.dataset.quantityBound !== 'true') {
            minusButton.addEventListener('click', () => {
              const { min, step } = getLimits();
              setValue(getNumber(input.value, min) - step);
            });
            minusButton.dataset.quantityBound = 'true';
          }
        }

        if (plusButton) {
          if (plusButton.dataset.quantityBound !== 'true') {
            plusButton.addEventListener('click', () => {
              const { min, step } = getLimits();
              setValue(getNumber(input.value, min) + step);
            });
            plusButton.dataset.quantityBound = 'true';
          }
        }

        if (input.dataset.quantityBound !== 'true') {
          input.addEventListener('change', () => {
            const { min } = getLimits();
            setValue(getNumber(input.value, min), false);
          });
          input.dataset.quantityBound = 'true';
        }
      }

      setupTrustpilotScroll() {
        const trustpilotLinks = Array.from(this.querySelectorAll('[data-scroll-to-trustpilot]'));
        if (!trustpilotLinks.length) return;

        trustpilotLinks.forEach((link) => {
          link.addEventListener('click', (event) => {
            const target = document.querySelector('[data-trustpilot-showcase]');
            if (!target) return;

            event.preventDefault();
            target.scrollIntoView({ behavior: 'smooth', block: 'start' });
          });
        });
      }
    }
  );
}
